// src/pages/AdminMethod.jsx
// STATUS: active | UPDATED: 2026-09-01
// LOG:
//   2026-09-01  New. What Jazz has told the coach about his own method.
//
// The other half of the interview. He talks, it writes things down, and this is
// where those land so a human decides what to do with them.
//
// NOTHING HERE IS LIVE YET, ON PURPOSE
//
// A captured word track does not change what the coach teaches until somebody
// builds it in. That is deliberate. A live conversation that can rewrite the
// curriculum is a curriculum that can be rewritten by accident, and the coach
// is already grounded in a knowledge base precisely so it cannot invent things.
//
// So this is a queue, and "Applied" means a person has folded it into
// j13Vocabulary.js or coachKnowledge.js and shipped it.
//
// SHOWN VERBATIM
//
// No truncation on the content, no tidying, no summarising. His exact words are
// the entire asset, and a preview that cuts them off is a preview that hides
// the thing we asked him for.

import { useCallback, useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Icon, Button, Badge,
  Spinner, Alert, AlertIcon, useToast,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Mic, Check, X, Quote } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { STEPS } from '../content/steps'

const KIND_COPY = {
  word_track: { label: 'Word track', note: 'Something he actually says. Highest value.' },
  correction: { label: 'Correction', note: 'The current content is wrong or incomplete.' },
  definition: { label: 'Definition', note: 'What a term means, in his words.' },
  idea: { label: 'Idea', note: 'Something the product should do.' },
  complaint: { label: 'Complaint', note: 'Something that annoyed him.' },
}

// Word tracks and corrections change what the coach teaches, so they sort
// above ideas, which are a conversation rather than a change.
const KIND_RANK = { word_track: 0, correction: 1, definition: 2, complaint: 3, idea: 4 }

function Contribution({ row, onDecide }) {
  const [busy, setBusy] = useState(false)
  const kind = KIND_COPY[row.kind] || { label: row.kind, note: '' }
  const step = row.step_number && STEPS.find((s) => s.number === row.step_number)
  const settled = row.status !== 'new'

  async function decide(status) {
    setBusy(true)
    const { error } = await supabase
      .from('method_contributions')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', row.id)
    setBusy(false)
    if (!error) onDecide(row.id, status)
  }

  return (
    <Box
      bg="card"
      border="1px solid"
      borderColor="line"
      borderLeftWidth={settled ? '1px' : '3px'}
      borderLeftColor={settled ? 'line' : 'accent.500'}
      borderRadius="card"
      p={{ base: 5, md: 6 }}
      opacity={row.status === 'declined' ? 0.55 : 1}
    >
      <HStack spacing={2} mb={2} wrap="wrap">
        <Text textStyle="eyebrow" color={settled ? 'inkMuted' : 'accent.500'}>
          {kind.label}
        </Text>
        {step && (
          <Text fontSize="12px" color="inkMuted">
            Step {step.number}, {step.title}
          </Text>
        )}
        {settled && (
          <Badge
            bg="surface" color="inkMuted" fontSize="10px" textTransform="uppercase"
            letterSpacing="0.08em" px={2} py={0.5} borderRadius="full" fontWeight={600}
          >
            {row.status}
          </Badge>
        )}
      </HStack>

      <Text fontSize="body" fontWeight={600} color="ink" mb={3} lineHeight={1.35}>
        {row.title}
      </Text>

      {/* Verbatim. A word track is worth exactly what his phrasing is worth. */}
      <HStack align="flex-start" spacing={3} bg="surface" borderRadius="md" p={4} mb={4}>
        <Box color="inkMuted" flexShrink={0} pt={0.5}>
          <Quote size={14} />
        </Box>
        <Text fontSize="body-sm" color="ink" lineHeight={1.7} whiteSpace="pre-wrap">
          {row.content}
        </Text>
      </HStack>

      {!settled && (
        <HStack spacing={2}>
          <Button
            size="sm" bg="ink" color="onInk" _hover={{ opacity: 0.88 }}
            leftIcon={<Check size={14} />} isLoading={busy}
            onClick={() => decide('applied')}
          >
            Built in
          </Button>
          <Button
            size="sm" variant="ghost" color="inkMuted"
            leftIcon={<X size={14} />} isLoading={busy}
            onClick={() => decide('declined')}
          >
            Not using it
          </Button>
        </HStack>
      )}
    </Box>
  )
}

export default function AdminMethod() {
  const toast = useToast()
  const [state, setState] = useState({ loading: true, rows: [], error: null })

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('method_contributions')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) {
      setState({ loading: false, rows: [], error: 'Could not load what he has told us.' })
      return
    }
    setState({ loading: false, rows: data || [], error: null })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function onDecide(id, status) {
    setState((s) => ({ ...s, rows: s.rows.map((r) => (r.id === id ? { ...r, status } : r)) }))
    toast({
      status: 'success',
      title: status === 'applied' ? 'Marked as built in' : 'Marked as not using it',
    })
  }

  const open = state.rows
    .filter((r) => r.status === 'new')
    .sort((a, b) => (KIND_RANK[a.kind] ?? 9) - (KIND_RANK[b.kind] ?? 9))
  const settled = state.rows.filter((r) => r.status !== 'new')

  return (
    <VStack align="stretch" spacing={6} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          Operator
        </Text>
        <Text textStyle="pageTitle">What Jazz has told us</Text>
        <Text fontSize="body" color="inkMuted" lineHeight={1.65} maxW="measure" mt={3}>
          Scripts, corrections and ideas captured while he talked to the coach, in his own
          words. Nothing here changes what the product teaches until somebody builds it in,
          which is what Built in means.
        </Text>
      </Box>

      {state.loading && (
        <Flex justify="center" py={10}>
          <Spinner size="md" color="ink" thickness="3px" />
        </Flex>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!state.loading && !state.error && state.rows.length === 0 && (
        <Box bg="surface" borderRadius="card" p={6}>
          <HStack spacing={3} mb={2}>
            <Icon as={Mic} boxSize={4} color="inkMuted" />
            <Text textStyle="eyebrow" color="inkMuted">
              Nothing yet
            </Text>
          </HStack>
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} maxW="measure" mb={4}>
            Everything the coach judges a salesperson against came out of the written manuals.
            Jazz has said those are close but not what he actually uses. Ten minutes of him
            talking fixes that for every floor.
          </Text>
          <Button as={RouterLink} to="/coach/?mode=method" size="sm" bg="ink" color="onInk" _hover={{ opacity: 0.88 }}>
            Start the interview
          </Button>
        </Box>
      )}

      {open.length > 0 && (
        <>
          <HStack spacing={2}>
            <Icon as={Mic} boxSize={4} color="accent.500" />
            <Text textStyle="eyebrow" color="accent.500">
              {open.length} waiting to be built in
            </Text>
          </HStack>
          {open.map((r) => <Contribution key={r.id} row={r} onDecide={onDecide} />)}
        </>
      )}

      {settled.length > 0 && (
        <>
          <HStack spacing={2} pt={4}>
            <Icon as={Check} boxSize={4} color="inkMuted" />
            <Text textStyle="eyebrow" color="inkMuted">
              Settled
            </Text>
          </HStack>
          {settled.map((r) => <Contribution key={r.id} row={r} onDecide={onDecide} />)}
        </>
      )}
    </VStack>
  )
}
